// =============================================
// Organization Filter Dropdown
// KipiKid - סינון לפי ארגון בממשק מנהל
// =============================================

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, Building2, X, Check } from 'lucide-react';
import { adminApi, type OrganizationDto } from '../../services/api';
import { useAdminStore } from '../../stores/adminStore';
import { adminColors as colors } from '../../styles/colors';

export default function OrganizationFilter() {
  const { selectedOrganizationId, selectedOrganizationName, setSelectedOrganization, clearOrganizationFilter } = useAdminStore();
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const result = await adminApi.getOrganizations();
      if (result.success && result.data) {
        setOrganizations(result.data);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.includes(searchQuery) || org.organizationTypeName.includes(searchQuery)
  );

  const handleSelect = (org: OrganizationDto | null) => {
    if (org) {
      setSelectedOrganization(org.publicId, org.name);
    } else {
      clearOrganizationFilter();
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', padding: '0 12px' }}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          backgroundColor: selectedOrganizationId ? '#DCFCE7' : colors.background,
          border: `1px solid ${selectedOrganizationId ? '#22c55e40' : colors.border}`,
          borderRadius: '10px',
          cursor: 'pointer',
          direction: 'rtl',
          transition: 'all 0.2s',
        }}
      >
        <Building2 size={16} color={selectedOrganizationId ? colors.success : colors.textLight} />
        <span style={{
          flex: 1,
          textAlign: 'right',
          fontSize: '13px',
          fontWeight: selectedOrganizationId ? '600' : '400',
          color: selectedOrganizationId ? '#166534' : colors.textLight,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {selectedOrganizationName || 'כל הארגונים'}
        </span>
        {selectedOrganizationId ? (
          <X
            size={14}
            color={colors.textLight}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(null);
            }}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <ChevronDown
            size={14}
            color={colors.textLight}
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: '12px',
              right: '12px',
              marginTop: '6px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              border: `1px solid ${colors.border}`,
              zIndex: 60,
              overflow: 'hidden',
              direction: 'rtl',
            }}
          >
            {/* Search Input */}
            <div style={{ padding: '10px', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="חיפוש ארגון..."
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '8px 32px 8px 10px',
                    fontSize: '13px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    direction: 'rtl',
                  }}
                />
                <Search
                  size={14}
                  color={colors.textLight}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                />
              </div>
            </div>

            {/* Options List */}
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              {/* "All" Option */}
              <button
                onClick={() => handleSelect(null)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 14px',
                  border: 'none',
                  backgroundColor: !selectedOrganizationId ? colors.primaryLight : 'white',
                  cursor: 'pointer',
                  borderBottom: `1px solid ${colors.border}`,
                  textAlign: 'right',
                }}
              >
                <Building2 size={16} color={!selectedOrganizationId ? colors.primary : colors.textLight} />
                <span style={{
                  flex: 1,
                  fontSize: '13px',
                  fontWeight: !selectedOrganizationId ? '600' : '400',
                  color: !selectedOrganizationId ? colors.primary : colors.text,
                }}>
                  כל הארגונים
                </span>
                {!selectedOrganizationId && <Check size={14} color={colors.primary} />}
              </button>

              {loading ? (
                <div style={{ padding: '16px', textAlign: 'center', color: colors.textLight, fontSize: '13px' }}>
                  טוען...
                </div>
              ) : filteredOrgs.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: colors.textLight, fontSize: '13px' }}>
                  לא נמצאו ארגונים
                </div>
              ) : (
                filteredOrgs.map(org => (
                  <button
                    key={org.publicId}
                    onClick={() => handleSelect(org)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 14px',
                      border: 'none',
                      backgroundColor: selectedOrganizationId === org.publicId ? colors.primaryLight : 'white',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: 'right',
                    }}
                  >
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      backgroundColor: selectedOrganizationId === org.publicId ? colors.primary + '15' : colors.background,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Building2 size={14} color={selectedOrganizationId === org.publicId ? colors.primary : colors.textLight} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: selectedOrganizationId === org.publicId ? '600' : '500',
                        color: selectedOrganizationId === org.publicId ? colors.primary : colors.text,
                      }}>
                        {org.name}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textLight }}>
                        {org.organizationTypeName} · {org.kindergartenCount} גנים
                      </div>
                    </div>
                    {selectedOrganizationId === org.publicId && <Check size={14} color={colors.primary} />}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
